import { useCallback } from 'react';
import type { Dispatch } from 'react';
import type { Step, StepResult } from '@de/types/types';
import type { SiteConfig } from '@de/types/config';
import type { LeadSubmissionConfig } from '@de/types/leadSubmission';
import type {
  FlowState,
  StepData,
  DynamicExperienceAction,
} from '../providers/dynamicExperienceReducer';
import { actionCreators } from '../providers/dynamicExperienceReducer';
import { LogicType } from '@de/types/enums';
import { evaluateCondition } from '@de/utils/getNextStep';
import {
  getAllSelectedOptions,
  getFullOptionsFromFlowState,
  getAllFlowFields,
  transformFlowStateToLeadInputs,
} from '@de/utils/dataFromFlowUtils';
import { resolveEjectUrl } from '@de/utils/generateUrl';
import { buildContextParams } from '@de/utils/buildContextParams';
import { emitConversion } from '@de/utils/conversionEvents';
import { handleContactSubmission } from '@de/utils/contactExtractors';
import { buildConversionCreationRequest } from '@de/utils/buildConversionCreationRequest';
import {
  initConversion,
  submitLead,
} from '@de/utils/data-fetch/postLeadSubmission';
import type { EventContext } from '@de/utils/createEventingPayloadsDE';
import {
  readStepFlowType,
  readStepMonetizationChannel,
  readStepTrafficSource,
} from '@de/utils/configurable-step';
import type { Option } from '../types/types';

const QUALIFICATIONS_NOT_MET_MESSAGE =
  'This program is not available for your profile.';
const GENERIC_SUBMISSION_ERROR_MESSAGE =
  'Something went wrong submitting your application. Please try again.';

class IncompleteLeadSubmissionError extends Error {
  constructor() {
    super('LDS did not accept the lead');
    this.name = 'IncompleteLeadSubmissionError';
  }
}

/**
 * Opens the result URL in a new tab. Uses top window when in an iframe so the
 * new tab opens in the parent. If the browser blocks the popup, calls
 * onPopupBlocked(url) so the host can show a message or offer same-tab.
 */
function openResultUrl(
  url: string,
  onPopupBlocked?: (url: string) => void,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const targetWindow = window.top ? window.top : window;
  const opened = targetWindow.open(url, '_blank', 'noopener,noreferrer');
  if (opened == null && onPopupBlocked) {
    onPopupBlocked(url);
  }
}

function resolveSubmissionError(error: unknown): string {
  if (error instanceof Error && error.message === 'qualifications_not_met') {
    return QUALIFICATIONS_NOT_MET_MESSAGE;
  }
  return GENERIC_SUBMISSION_ERROR_MESSAGE;
}

interface UseHandleSubmitParams {
  flowStateRef: React.MutableRefObject<FlowState>;
  currentStep?: Step;
  steps: Step[];
  currentStepIndex: number;
  currentStepData: Partial<StepData> & Record<string, unknown>;
  correlationId: string;
  siteConfig?: SiteConfig;
  fromStepIndex: number;
  formViewedCorrelationId: string;
  dispatch: Dispatch<DynamicExperienceAction>;
  /** When provided, FormSubmitted is fired when the flow completes and navigates to the result URL */
  triggerFormEvents?: (eventName: string, context: EventContext) => void;
  triggerIdentify?: (
    flowState: FlowState,
    dcsData?: { degree?: Option; category?: Option; subject?: Option },
  ) => void;
  eventContext?: EventContext;
  leadSubmissionConfig?: LeadSubmissionConfig;
  /** When provided, called when window.open is blocked so the host can show a message or offer same-tab */
  onPopupBlocked?: (url: string) => void;
  /** Allowlisted `customField` names from `entryPoint.forwardFields` (Discover `ctx_*`). */
  forwardFields?: string[];
}

export function useHandleSubmit({
  flowStateRef,
  currentStep,
  steps,
  currentStepIndex,
  currentStepData,
  correlationId,
  siteConfig,
  fromStepIndex,
  formViewedCorrelationId,
  dispatch,
  triggerFormEvents,
  triggerIdentify,
  eventContext,
  leadSubmissionConfig,
  onPopupBlocked,
  forwardFields,
}: UseHandleSubmitParams) {
  return useCallback(
    async (result?: Step, redirectUrl?: string): Promise<void> => {
      const stepToUse = result || currentStep;
      if (!stepToUse) return;

      // A redirecting entry's form event is emitted by `useForwardStepEventFiring`,
      // so suppress the terminal `FormSubmitted` here to avoid double-signalling.
      // Non-entry redirects and ordinary terminal submits are unchanged.
      const isEntryRedirect = Boolean(currentStep?.isEntry && redirectUrl);

      const latestFlowState = flowStateRef.current;

      // Modal gate
      const tcpaAccepted = latestFlowState['TCPA']?.accepted === true;
      if (stepToUse.modal?.enable && !tcpaAccepted) {
        dispatch(actionCreators.setOpenModal(true));
        return;
      }

      // Find the result step via branching logic
      const resultStepId = stepToUse.branchingLogic?.find(
        (bl) =>
          bl.type === LogicType.RESULT &&
          evaluateCondition(
            bl,
            currentStepData,
            getAllSelectedOptions(latestFlowState),
            stepToUse,
          ),
      )?.nextId;

      await handleContactSubmission({
        flowState: latestFlowState,
        correlationId: correlationId,
        publisher: siteConfig?.domain || 'Unknown',
      });

      const resultStep =
        result || steps?.find((step) => step.id === resultStepId);

      const dcsData = {
        degree: getFullOptionsFromFlowState('degree', latestFlowState),
        category: getFullOptionsFromFlowState('category', latestFlowState),
        subject: getFullOptionsFromFlowState('subject', latestFlowState),
      };

      const contextParams = buildContextParams(
        latestFlowState,
        steps,
        forwardFields,
      );

      if (triggerIdentify) {
        const emailConsentValue = getFullOptionsFromFlowState(
          'emailConsent',
          latestFlowState,
        )?.value as string;
        const hasEmailConsent =
          emailConsentValue === 'Yes' || emailConsentValue === 'true';
        const hasEmail = !!(getFullOptionsFromFlowState(
          'email',
          latestFlowState,
        )?.value as string);
        const hasFirstName = !!(getFullOptionsFromFlowState(
          'firstName',
          latestFlowState,
        )?.value as string);

        if (hasEmail && (hasEmailConsent || hasFirstName)) {
          triggerIdentify(latestFlowState, dcsData);
        }
      }

      // Lead submission path: when leadSubmissionConfig is present
      if (leadSubmissionConfig) {
        dispatch(actionCreators.setSubmissionError(null));
        dispatch(actionCreators.setIsSubmitting(true));

        try {
          const locationState = latestFlowState['location'] as
            | { countryCode?: string; state?: string }
            | undefined;
          const monetizationChannel = readStepMonetizationChannel(
            resultStep || stepToUse,
          );
          const conversion = buildConversionCreationRequest(
            leadSubmissionConfig.result,
            siteConfig ?? ({} as SiteConfig),
            correlationId,
            {
              flowType: readStepFlowType(resultStep || stepToUse),
              trafficSource: readStepTrafficSource(resultStep || stepToUse),
              monetizationChannel,
              location: {
                country: locationState?.countryCode,
                state: locationState?.state,
              },
            },
          );

          const conversionResponse = await initConversion(conversion);

          const inputs = transformFlowStateToLeadInputs(latestFlowState);
          const leadResponse = await submitLead({
            inputs,
            record: leadSubmissionConfig.record,
            result: leadSubmissionConfig.result,
            conversionId: conversionResponse.conversionId,
            activeProspectToken: leadSubmissionConfig.activeProspectToken,
            jornayaToken: leadSubmissionConfig.jornayaToken,
            needsActiveProspectToken:
              leadSubmissionConfig.needsActiveProspectToken,
            productCorrelationId: correlationId,
            identityToken: siteConfig?.identityToken,
            monetizationChannel,
          });
          if (leadResponse.leadSubmitted === false) {
            throw new IncompleteLeadSubmissionError();
          }

          try {
            emitConversion({
              name: 'Lead',
              pageUrl:
                typeof window !== 'undefined'
                  ? window.location.href
                  : (siteConfig?.domain ?? ''),
            });
          } catch {
            // Marketing emit must not surface as a failed lead after LDS accepted.
          }

          const { url } = resolveEjectUrl({
            step: (resultStep || stepToUse) as Step & StepResult,
            currentStepId: currentStep?.id || '',
            correlationId,
            siteConfig,
            dcsData,
            redirectUrl,
            fromStepIndex,
            formViewedCorrelationId,
            contextParams,
          });

          if (url && triggerFormEvents && eventContext && !isEntryRedirect) {
            triggerFormEvents('FormSubmitted', {
              ...eventContext,
              outboundUrl: url,
              currentStep: stepToUse,
              field: getAllFlowFields(latestFlowState, steps),
            });
          }

          if (url) {
            openResultUrl(url, onPopupBlocked);
          }

          // Eject-on-submit (DCS or DSL `discover`): the caller passes
          // a `redirectUrl` to signal "open the result URL in a new tab
          // and leave the user where they are". Advancing the local
          // flowState past the question step would visually replace the
          // question with the result step in the original tab even
          // though the user's actual destination is the new tab — keep
          // the original window pinned to the eject step so they can
          // come back to it.
          if (!redirectUrl) {
            const nextStep = resultStep || stepToUse;
            const newStepIndex = currentStepIndex + 1;
            dispatch(actionCreators.setCurrentStep(nextStep));
            dispatch(actionCreators.setCurrentStepIndex(newStepIndex));
            dispatch(actionCreators.addToNavigationHistory(nextStep));
          }
        } catch (error) {
          dispatch(
            actionCreators.setSubmissionError(resolveSubmissionError(error)),
          );
          return;
        } finally {
          dispatch(actionCreators.setIsSubmitting(false));
        }
        return;
      }

      // URL-only path: no lead submission (existing behavior)
      const { url } = resolveEjectUrl({
        step: (resultStep || stepToUse) as Step & StepResult,
        currentStepId: currentStep?.id || '',
        correlationId,
        siteConfig,
        dcsData,
        redirectUrl,
        fromStepIndex,
        formViewedCorrelationId,
        contextParams,
      });

      // Fire FormSubmitted on completion with a result URL. Skipped for a
      // redirecting entry (the firer already emitted its form event).
      if (
        eventContext &&
        triggerFormEvents &&
        stepToUse?.isTerminal &&
        !isEntryRedirect
      ) {
        triggerFormEvents('FormSubmitted', {
          ...eventContext,
          outboundUrl: url,
          currentStep: stepToUse,
          field: getAllFlowFields(latestFlowState, steps),
        });
      }

      // Open the result URL in a new tab if available (with popup-blocked callback)
      if (url) {
        openResultUrl(url, onPopupBlocked);
      }

      // Eject-on-submit (DCS or DSL `discover`): see the matching guard
      // in the lead-submission branch above for why we skip the
      // step-advance dispatches when the caller passed a `redirectUrl`.
      if (!redirectUrl) {
        const nextStep = resultStep || stepToUse;
        const newStepIndex = currentStepIndex + 1;
        dispatch(actionCreators.setCurrentStep(nextStep));
        dispatch(actionCreators.setCurrentStepIndex(newStepIndex));
        dispatch(actionCreators.addToNavigationHistory(nextStep));
      }
    },
    [
      correlationId,
      steps,
      currentStep,
      currentStepIndex,
      currentStepData,
      siteConfig,
      fromStepIndex,
      formViewedCorrelationId,
      flowStateRef,
      dispatch,
      triggerFormEvents,
      triggerIdentify,
      eventContext,
      leadSubmissionConfig,
      onPopupBlocked,
      forwardFields,
    ],
  );
}
